import { ThemeProvider, createTheme } from '@mui/material';
import { BrowserTracing } from '@sentry/browser';
import * as Sentry from '@sentry/react';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import React from 'react';
import ReactDOM from 'react-dom/client';
import ReactGA from 'react-ga';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';

const TRACKING_ID = 'UA-255947352-1';

ReactGA.initialize(TRACKING_ID);
ReactGA.send({ hitType: 'pageview', page: window.location.pathname });

Sentry.init({
  dsn: 'https://02998e08309948d38a725bfc18c7f504@o4504445782851584.ingest.sentry.io/4504445804085248',
  integrations: [new BrowserTracing(), new Sentry.Replay()],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

const root = ReactDOM.createRoot(rootElement);
const queryClient = new QueryClient();

const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#FF006E',
    },
    secondary: {
      main: '#FFBE0B',
    },
  },
  typography: {
    fontFamily: 'Inter',
  },
});

const app = (
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <BrowserRouter>
        <ErrorBoundary>
          <ThemeProvider theme={theme}>
            <RecoilRoot>
              <App />
            </RecoilRoot>
          </ThemeProvider>
        </ErrorBoundary>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
    <Toaster
      position="bottom-center"
      toastOptions={{
        style: {
          padding: '1rem',
        },
      }}
    />
  </React.StrictMode>
);

root.render(app);
